/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  forwardRef,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
// local imports
import { useStyles } from '~src/hooks/useStyles';
import { useTenderSharesList } from '~src/hooks/api-hooks/useTenderSharesList';
import { useSecondaryShareKeys } from '~src/hooks/api-hooks/useSecondaryShareKeys';
import { DbShare } from '~src/types/db';
import { DB_STATE, TENDER_REGISTRATION_STATE } from '~src/types/enums';
import { TenderTabsProps } from '~components/molecules/Tabs/TenderTabs/TenderTabs';
import { TenderSharesListTable } from '~components/atoms/FileTables/TenderShares/TenderSharesListTable';
import { styleFn } from '../../TenderTabs.styles';
import { useStore } from '~src/store';
import { getUiShareKeyStatus } from '~src/utils/shareUtils/useShareKeysStatusUtils';
import { TableRowSelection } from 'antd/lib/table/interface';
import { TableEntry } from '~components/atoms/FileTables/FileTable.utils';
import { appLog } from '~src/utils/log';
import { SearchContext } from '~src/contexts/SearchContext';
import { filterByQuery } from '~src/utils/searchFilters';
import { isTenderClosed } from '~src/utils/isTenderClosed';

const DEFAULT_STATES: readonly DB_STATE[] = [
  DB_STATE.DRAFT,
  DB_STATE.AVAILABLE,
  DB_STATE.ACCEPTED,
  DB_STATE.REJECTED,
  DB_STATE.REMOVED,
];

type TenderSharesBlock = Pick<
  TenderTabsProps,
  | 'onSelectSharedKeys'
  | 'onSelectAllSharedKeys'
  | 'selectedSharedKeys'
  | 'highlightedRow'
  | 'focusOptions'
  | 'scroll'
> & {
  // Admin identity - the numeric registration id, used in
  // /projects/{id}/tenders/{id}/... paths. Optional because the vendor
  // call site (My-Tenders -> Subcontractors tab) has no such id, only a
  // GUID share_id/share_key (see shareId/shareKey below).
  tenderId?: string;
  projectId?: number;
  // Vendor identity (My-Tenders -> Subcontractors tab) - the GUID share_id
  // used in /tender_shares/{id}/... paths, and the acting Primary-Share-Key's
  // own Share-Key header. Mirrors ContractAndOffer.tsx's isOnMyTender pattern.
  shareId?: string;
  shareKey?: string;
  // Primary-/Secondary Share Keys (vendor tab): the Invite trigger button
  // itself now lives in MyTendersTabs.tsx's header (tabBarExtraContent),
  // not inside this block or the table - these are just passed straight
  // through to TenderSharesListTable, which still owns the Modal/mutation.
  isInviteModalOpen?: boolean;
  onCloseInviteModal?: () => void;
  onSelect?: TableRowSelection<TableEntry>['onSelect'];
  onSelectAll?: TableRowSelection<TableEntry>['onSelectAll'];
  setSelectedItems: Dispatch<SetStateAction<TableEntry[]>>;
  selectedKeys?: TableRowSelection<TableEntry>['selectedRowKeys'];
  bottomViewHeight?: number;
  tenderRegData?: any;
};

export const TenderSharesBlock = forwardRef<HTMLDivElement, TenderSharesBlock>(
  (
    {
      tenderId,
      projectId,
      shareId,
      shareKey,
      isInviteModalOpen,
      onCloseInviteModal,
      highlightedRow,
      focusOptions,
      selectedSharedKeys,
      onSelectSharedKeys,
      onSelectAllSharedKeys,
      onSelect,
      onSelectAll,
      setSelectedItems,
      selectedKeys,
      bottomViewHeight = 0,
      tenderRegData,
      ...tableProps
    },
    ref
  ) => {
    const styles = useStyles(styleFn);

    const { filterStates, shareKeysInviteFilter } = useStore();
    const searchContext = useContext(SearchContext);

    // Primary-/Secondary Share Keys: vendor mode (My-Tenders -> Subcontractors
    // tab) is active when both shareId/shareKey are present - exact same
    // expression ContractAndOffer.tsx already uses to share one component
    // between the Project-admin side and the vendor's My-Tenders side.
    const isOnMyTender = !!shareId && !!shareKey;

    const [statesToDisplay, setStatesToDisplay] = useState<readonly DB_STATE[]>(
      DEFAULT_STATES
    );
    const [shareKeysList, setShareKeysList] = useState<DbShare[]>([]);
    const shareTendersQuery = useTenderSharesList(
      {
        projectId,
        tenderId,
        // Add the 'statesToDisplay' as a param in case the implementation of states will move to server side
        states: DEFAULT_STATES.map((value) => value),
      },
      // Admin-only query - must be disabled in vendor mode, otherwise it
      // would keep firing with projectId/tenderId both undefined (this
      // hook has no truthiness guard of its own).
      { enabled: !isOnMyTender }
    );

    // Primary-/Secondary Share Keys: vendor mode's own data source - the
    // acting Primary-Share-Key's subcontractors, via the Share-Key header.
    // Must explicitly request every valid secondary-key state (including
    // REMOVED) here, same as the admin query above - the backend's GET
    // /secondary_share_keys endpoint does not return removed rows unless
    // their state is explicitly listed, so omitting `states` silently
    // hides a subcontractor right after it's deleted (it should still show
    // up in the list with a "Removed" status instead of disappearing).
    const secondaryShareKeysQuery = useSecondaryShareKeys({
      shareId: shareId || '',
      shareKey: shareKey || '',
      states: DEFAULT_STATES.map((value) => value),
    });

    const handleChangeShowRejected = () => {
      appLog(`1`);
    };
    // Client side implementation of states filtering due to the small expected amount of keys
    // and reducing requests quantity
    useEffect(() => {
      if (Array.isArray(shareTendersQuery.data)) {
        const listAfterFilter = shareTendersQuery.data.filter((share) =>
          filterStates.includes(getUiShareKeyStatus(share))
        );
        setShareKeysList(listAfterFilter);
      }
    }, [filterStates, shareTendersQuery.data]);

    // Primary-/Secondary Share Keys: vendor mode's own filtered list - kept
    // as a separate memo rather than piggy-backing on the admin useEffect
    // above, since that effect only ever re-runs off shareTendersQuery.data
    // (it would never notice updates to the vendor query). The boundary
    // cast is safe here: every field useColumns.tsx/getShareStatusVisualIntl
    // read under viewOnly+hideContractsColumn (share_key_state, title,
    // email, claimer, opened/claimed/first_invite_at) exists verbatim on
    // SecondaryShareKeyVM - confirmed field-by-field against db.d.ts.
    const vendorShareKeysList = useMemo(() => {
      if (!isOnMyTender) return [];
      const raw = ((secondaryShareKeysQuery.data ??
        []) as unknown) as DbShare[];
      return raw.filter((share) =>
        filterStates.includes(getUiShareKeyStatus(share))
      );
    }, [isOnMyTender, secondaryShareKeysQuery.data, filterStates]);

    const filteredShareKeysList = useMemo(() => {
      const baseList = isOnMyTender ? vendorShareKeysList : shareKeysList;
      return baseList.filter((share) => {
        if (
          filterByQuery(share.share_key_email, searchContext.query) ||
          filterByQuery(share.share_description, searchContext.query) ||
          filterByQuery(share.share_key_title, searchContext.query)
        ) {
          return share;
        }
        return null;
      });
    }, [isOnMyTender, vendorShareKeysList, shareKeysList, searchContext.query]);

    // Primary-/Secondary Share Keys: secondary rows must never show as
    // their own top-level rows - they're rendered read-only inside the
    // drawer opened from their primary row (SecondaryShareKeysDrawer)
    // instead, now fetched live via useAdminSecondaryShareKeys.ts. Backend's
    // Tender Admin - Secondary Share Keys Update confirmed this admin
    // listing (GET /projects/{id}/tenders/{id}/share_keys) returns primary
    // rows only, so this filter is expected to be a no-op today - kept as a
    // cheap defensive guard in case that ever changes, rather than removed.
    // Vendor mode's filteredShareKeysList is ALREADY the vendor's own
    // subcontractors list (every row of which has share_key_is_primary ===
    // false and a share_key_parent_id set, by definition) - running it
    // through the primary-only filter below would exclude every single
    // row. So primaryShareKeys is only ever used for admin mode; vendor
    // mode uses filteredShareKeysList directly (see shareKeysList prop
    // below).
    const primaryShareKeys = useMemo(() => {
      if (isOnMyTender) return [];
      const primaryOnly = filteredShareKeysList.filter(
        (share) =>
          !(share.share_key_is_primary === false && share.share_key_parent_id)
      );
      // Primary-/Secondary Share Keys: admin Shares-tab filter (TenderActions.tsx's
      // new Radio.Group) - "withSecondaryInvites" narrows the list down to
      // primary vendors that have actually invited at least one
      // subcontractor. Orthogonal to the state-checkbox filter above.
      if (shareKeysInviteFilter === 'withSecondaryInvites') {
        return primaryOnly.filter(
          (share) => share.has_secondary_share_keys === true
        );
      }
      return primaryOnly;
    }, [isOnMyTender, filteredShareKeysList, shareKeysInviteFilter]);

    // Confirmed via GET /projects/{project_id}/tenders/{tender_id} - the
    // Primary-/Secondary Share Keys activation flag lives on the tender/share
    // object (tenderRegData), not on individual share_keys list items. When
    // off, the "+" expand column should not render at all on the shares table.
    const isSecondaryKeysOn = Boolean(tenderRegData?.share_is_secondary_key_on);

    // Primary-/Secondary Share Keys: blocks inviting new subcontractors
    // from the drawer/tab once the tender is closed, matching the same
    // rule already enforced for the primary Vendors "Invite" flow. Vendor
    // mode uses the shared isTenderClosed helper (same one ContractAndOffer.tsx
    // uses) for consistency between both modes; admin mode keeps its
    // existing raw state comparison unchanged.
    const tenderCompleted = isOnMyTender
      ? isTenderClosed(tenderRegData)
      : tenderRegData?.state === TENDER_REGISTRATION_STATE.CLOSED;

    return (
      <div css={styles.blockContainer}>
        <TenderSharesListTable
          {...tableProps}
          shareKeysList={
            isOnMyTender ? filteredShareKeysList : primaryShareKeys
          }
          isSecondaryKeysOn={isSecondaryKeysOn}
          tenderCompleted={tenderCompleted}
          isLoading={
            isOnMyTender
              ? secondaryShareKeysQuery.isLoading
              : shareTendersQuery.isLoading
          }
          highlightedRow={highlightedRow}
          tenderId={tenderId}
          projectId={projectId}
          shareId={shareId}
          shareKey={shareKey}
          isOnMyTender={isOnMyTender}
          isInviteModalOpen={isInviteModalOpen}
          onCloseInviteModal={onCloseInviteModal}
          setSelectedItems={setSelectedItems}
          onSelect={onSelect}
          onSelectAll={onSelectAll}
          selectedKeys={selectedKeys}
          selectedSharedKeys={selectedSharedKeys}
          onSelectSharedKeys={onSelectSharedKeys}
          onSelectAllSharedKeys={onSelectAllSharedKeys}
          viewOnly={false}
          handleChangeShowRejected={handleChangeShowRejected}
          // scroll={{ y: `calc(100vh - 250px - ${bottomViewHeight}px)` }}
        />
        {/* PRINT ONLY COMPONENT - intentionally not given isSecondaryKeysOn/
            expandable rows: it's a hidden, non-interactive print layout, so
            nothing would ever be expanded on it anyway. Also skipped
            entirely in vendor mode - no print requirement for the
            Subcontractors tab per the ticket. */}
        {!isOnMyTender && (
          <div css={styles.printContainer}>
            <TenderSharesListTable
              {...tableProps}
              shareKeysList={filteredShareKeysList}
              isLoading={shareTendersQuery.isLoading}
              css={styles.printTable}
              ref={ref}
              highlightedRow={highlightedRow}
              tenderId={tenderId}
              projectId={projectId}
              setSelectedItems={setSelectedItems}
              viewOnly
            />
          </div>
        )}
      </div>
    );
  }
);
